import { getDeviceTimezone } from '@/utils/date';
import { NetworkStatus } from '@apollo/client';
import { transformToAccountListRowDataPaginated } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/transformers/account-list-transformers';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useGeneratedSalesPortalAccountListDataPaginatedQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/documents/sales-portal-account-list-data-paginated.generated';
import type { AccountListRowData } from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';
import type {
  SalesPortalAccountListDataPaginatedQueryVariables,
  SalesPortalAccountListFilters,
} from '@/types';

export const ACCOUNT_LIST_PAGE_SIZE = 50;

interface UseAccountListTablePaginatedLoaderParams {
  businessOrganizationUUID: string;
  filters?: SalesPortalAccountListFilters;
}

interface UseAccountListTablePaginatedLoaderResult {
  accounts: AccountListRowData[];
  canFetchMore: boolean;
  error: Error | null;
  fetchingMore: boolean;
  fetchMore: () => void;
  fetchMoreError: boolean;
  loading: boolean;
  refetch: () => void;
}

export function useAccountListTablePaginatedLoader({
  businessOrganizationUUID,
  filters,
}: UseAccountListTablePaginatedLoaderParams): UseAccountListTablePaginatedLoaderResult {
  const fetchingRef = useRef(false);
  const [fetchMoreError, setFetchMoreError] = useState(false);

  const userTimezone = getDeviceTimezone();
  const baseVariables =
    useMemo<SalesPortalAccountListDataPaginatedQueryVariables>(
      () => ({
        businessOrganizationUUID,
        filters,
        pageSize: ACCOUNT_LIST_PAGE_SIZE,
        userTimezone,
      }),
      [businessOrganizationUUID, filters, userTimezone],
    );

  const { data, error, fetchMore, networkStatus, refetch } =
    useGeneratedSalesPortalAccountListDataPaginatedQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: () => 'cache-first',
      notifyOnNetworkStatusChange: true,
      variables: baseVariables,
    });

  const page = data?.getSalesPortalAccountListInfoPaginated;
  const nextCursor = page?.cursor ?? null;

  const handleFetchMore = useCallback(async () => {
    if (fetchingRef.current || !nextCursor) {
      return;
    }
    fetchingRef.current = true;
    setFetchMoreError(false);
    try {
      await fetchMore({ variables: { cursor: nextCursor } });
    } catch {
      setFetchMoreError(true);
    } finally {
      fetchingRef.current = false;
    }
  }, [fetchMore, nextCursor]);

  const accounts = useMemo(
    () => (page ? transformToAccountListRowDataPaginated(page.accounts) : []),
    [page],
  );

  return {
    accounts,
    canFetchMore: nextCursor !== null,
    error: error ?? null,
    fetchingMore: networkStatus === NetworkStatus.fetchMore,
    fetchMore: handleFetchMore,
    fetchMoreError,
    loading:
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.setVariables ||
      networkStatus === NetworkStatus.refetch,
    refetch,
  };
}
